import React, { useEffect, useMemo, useState } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { useDataScope } from "@/hooks/useDataScope";
import { formatDate } from "@/lib/format";
import { DISPOSITIONS, DISPOSITION_META, DISPOSITION_LABEL, CONDITION_LABEL } from "@/lib/returns";
import { toast } from "sonner";
import {
  RotateCcw, Loader2, PackageCheck, ImageIcon, X, Layers, Search, Download, MapPin, Pencil,
  PackageX, HandHeart, Trash2, PauseCircle, Send, ChevronLeft, ChevronRight, Sparkles,
} from "lucide-react";

const DISP_ICONS = { PackageCheck, PackageX, HandHeart, Trash2, PauseCircle, Send };

// Prefer the backend's cookie-free signed URL (works cross-domain / custom
// domains); fall back to the legacy cookie-authed path for older payloads.
const photoUrl = (p) => p?.url || `${API_BASE}/claims/files/${p?.storage_path}`;

function CondChip({ value }) {
  return (
    <span className="inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full border bg-slate-100 text-slate-700 border-slate-200">
      {CONDITION_LABEL(value)}
    </span>
  );
}

function DispositionChooser({ onPick, saving, suggested }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="disposition-buttons">
      {DISPOSITIONS.map((d) => {
        const Icon = DISP_ICONS[d.icon] || PackageCheck;
        const isSuggested = suggested === d.value;
        return (
          <button
            key={d.value}
            disabled={saving}
            onClick={() => onPick(d.value)}
            data-testid={`disp-btn-${d.value}`}
            title={d.hint}
            className={`relative inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-semibold transition-all disabled:opacity-50 ${d.cls} hover:scale-[1.03] ${isSuggested ? "ring-2 ring-offset-1 ring-slate-900" : ""}`}
          >
            <Icon className="w-4 h-4 shrink-0" />
            {d.label}
            {isSuggested && <span className="ml-1 text-[10px] font-bold uppercase bg-slate-900 text-white px-1.5 py-0.5 rounded-full">Suggested</span>}
          </button>
        );
      })}
    </div>
  );
}

function DispositionBanner({ value, onChange, saving }) {
  const d = DISPOSITION_META[value] || {};
  const Icon = DISP_ICONS[d.icon] || PackageCheck;
  return (
    <div className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3 ${d.active || "bg-slate-700 text-white border-slate-700"}`} data-testid="disposition-banner">
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wider font-semibold opacity-80 leading-none">Dispositioned</p>
          <p className="text-base font-extrabold leading-tight truncate">{DISPOSITION_LABEL(value)}</p>
        </div>
      </div>
      <button onClick={onChange} disabled={saving} data-testid="disp-change-btn"
        className="text-xs font-semibold bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-md shrink-0 inline-flex items-center gap-1">
        <Pencil className="w-3.5 h-3.5" /> Change
      </button>
    </div>
  );
}

function ReturnItem({ item, onDisposition, onPhoto, saving }) {
  const disp = item.customer_disposition;
  const [editing, setEditing] = useState(false);
  const showChooser = !disp || editing;

  const pick = async (v) => {
    await onDisposition(item.id, v);
    setEditing(false);
  };

  return (
    <div className="flex gap-4 py-4 border-t border-slate-100 first:border-t-0" data-testid={`return-item-${item.id}`}>
      {/* Thumbnail / photos column */}
      <div className="shrink-0">
        {(item.photos || []).length > 0 ? (
          <button onClick={() => onPhoto(item.photos, 0)} data-testid={`return-photo-${item.photos[0].id}`}
            className="relative w-16 h-16 rounded-xl overflow-hidden border border-slate-200 hover:border-blue-400 transition-colors block">
            <img src={photoUrl(item.photos[0])} alt={item.name} className="w-full h-full object-cover" />
            {item.photos.length > 1 && (
              <span className="absolute bottom-0.5 right-0.5 bg-black/70 text-white text-[10px] font-bold px-1.5 rounded">+{item.photos.length - 1}</span>
            )}
          </button>
        ) : (
          <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center text-slate-300">
            <ImageIcon className="w-6 h-6" />
          </div>
        )}
      </div>

      {/* Details + disposition */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-bold text-slate-900 leading-tight">{item.name || item.sku || "Item"}</p>
            <p className="text-xs text-slate-500 font-mono mt-0.5">{item.sku}{item.barcode ? ` · ${item.barcode}` : ""}</p>
          </div>
          <span className="inline-flex items-center text-sm font-bold text-slate-700 bg-slate-100 px-2.5 py-1 rounded-full shrink-0">×{item.qty_returned}</span>
        </div>

        {(item.condition_tags || []).length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {item.condition_tags.map((t) => <CondChip key={t} value={t} />)}
          </div>
        )}

        <div className="mt-3">
          {showChooser ? (
            <>
              <p className="text-xs uppercase tracking-wider font-semibold text-slate-400 mb-2">What should we do with it?</p>
              {item.suggested_disposition && (
                <p className="text-xs text-slate-500 mb-2 flex items-center gap-1.5" data-testid={`disp-suggestion-${item.id}`}>
                  <Sparkles className="w-3.5 h-3.5 text-slate-400" />
                  Based on its condition, we suggest <span className="font-bold text-slate-700">{DISPOSITION_LABEL(item.suggested_disposition)}</span>.
                </p>
              )}
              <DispositionChooser onPick={pick} saving={saving} suggested={item.suggested_disposition} />
              {editing && (
                <button onClick={() => setEditing(false)} className="text-xs text-slate-400 hover:text-slate-600 mt-2" data-testid="disp-cancel-btn">Cancel</button>
              )}
            </>
          ) : (
            <DispositionBanner value={disp} onChange={() => setEditing(true)} saving={saving} />
          )}
        </div>

        {disp === "restock" && !editing && (
          <div className="mt-2.5 flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-sm text-emerald-800" data-testid={`restock-location-${item.id}`}>
            <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
            {(item.stock_locations || []).length > 0 ? (
              <span>Put it back to <span className="font-bold font-mono">{item.stock_locations[0].location_code}</span>
                {item.stock_locations.length > 1 ? ` (or ${item.stock_locations.slice(1).map((l) => l.location_code).join(", ")})` : ""}</span>
            ) : (
              <span>No prior location on file — staff will assign a bin on restock.</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ReturnCard({ r, onDisposition, onPhoto, onDownloadPhotos, savingItem }) {
  const units = (r.items || []).reduce((s, it) => s + (it.qty_returned || 0), 0);
  const pending = (r.items || []).filter((it) => !it.customer_disposition).length;
  const photoCount = (r.items || []).reduce((s, it) => s + (it.photos?.length || 0), 0);
  const visibleItems = useMemo(
    () => (r.items || []).filter((it) => (it.qty_returned || 0) > 0),
    [r.items]
  );
  return (
    <div data-testid={`return-card-${r.id}`} className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <p className="text-lg font-bold text-slate-900 font-mono">{r.order_number}</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Received {formatDate(r.completed_at || r.created_at)}{r.customer_name ? ` · ${r.customer_name}` : ""}{r.reason ? ` · ${r.reason}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {photoCount > 0 && (
            <button onClick={() => onDownloadPhotos(r)} data-testid={`return-download-photos-${r.id}`}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 border border-slate-200 px-2.5 py-1 rounded-full transition-colors"
              title="Download all photos for this return">
              <Download className="w-3.5 h-3.5" /> {photoCount} photo{photoCount === 1 ? "" : "s"}
            </button>
          )}
          {pending > 0
            ? <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full">{pending} to disposition</span>
            : <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full"><PackageCheck className="w-3.5 h-3.5" /> Dispositioned</span>}
          <span className="text-sm font-semibold text-slate-500">{units} unit{units === 1 ? "" : "s"}</span>
        </div>
      </div>

      <div data-testid="return-items">
        {visibleItems.map((it) => (
          <ReturnItem key={it.id} item={it} onDisposition={onDisposition} onPhoto={onPhoto} saving={savingItem === it.id} />
        ))}
      </div>
    </div>
  );
}

export default function Returns() {
  const { param, allMode } = useDataScope();
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [zoom, setZoom] = useState(false);
  const [search, setSearch] = useState("");
  const [savingItem, setSavingItem] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = () => {
    setLoading(true);
    setError(false);
    const qs = param ? `?${param}` : "";
    return api.get(`/returns${qs}`)
      .then(({ data }) => setReturns(data.returns || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    const qs = param ? `?${param}` : "";
    api.get(`/returns${qs}`)
      .then(({ data }) => { if (!cancelled) setReturns(data.returns || []); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [param]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") { setZoom(false); setLightbox(null); }
      else if (e.key === "ArrowRight") setLightbox((lb) => (lb && lb.photos.length > 1 ? { ...lb, index: (lb.index + 1) % lb.photos.length } : lb));
      else if (e.key === "ArrowLeft") setLightbox((lb) => (lb && lb.photos.length > 1 ? { ...lb, index: (lb.index - 1 + lb.photos.length) % lb.photos.length } : lb));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  const setDisposition = async (returnId, itemId, value) => {
    setSavingItem(itemId);
    try {
      const { data } = await api.patch(`/returns/${returnId}/items/${itemId}/disposition`, { disposition: value });
      setReturns((arr) => arr.map((r) => (r.id === returnId ? data : r)));
      toast.success(`Set to ${DISPOSITION_LABEL(value)}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingItem(null);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const qs = param ? `?${param}` : "";
      const { data } = await api.get(`/returns/export${qs}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "returns.csv"; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExporting(false);
    }
  };

  const downloadPhotos = async (r) => {
    try {
      const { data } = await api.get(`/returns/${r.id}/photos.zip`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url; a.download = `return_${r.order_number}_photos.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return returns;
    return returns.filter((r) =>
      (r.order_number || "").toLowerCase().includes(q) ||
      (r.customer_name || "").toLowerCase().includes(q));
  }, [returns, search]);

  return (
    <div className="space-y-6" data-testid="returns-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="inline-block text-xs uppercase tracking-wider font-semibold text-orange-600 bg-orange-100 px-2 py-0.5 rounded">Returns</span>
          <h1 className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: "Manrope" }}>Processed Returns</h1>
          <p className="text-slate-500 mt-1">Review each returned item&apos;s condition and photos, then tell us what to do with it.</p>
        </div>
        {!allMode && returns.length > 0 && (
          <button onClick={exportCsv} disabled={exporting} data-testid="returns-export-btn"
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white text-sm font-semibold px-4 py-2.5 rounded-lg">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Export to Excel
          </button>
        )}
      </div>

      {!allMode && (
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input data-testid="returns-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by order number or customer name…"
            className="w-full pl-10 pr-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400/40" />
        </div>
      )}

      {allMode ? (
        <div data-testid="returns-all-mode-prompt" className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-4 text-sm text-blue-800">
          <Layers className="w-5 h-5 shrink-0 mt-0.5" /> Pick a specific store from the top bar to see its returns.
        </div>
      ) : loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-16 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading returns…</div>
      ) : error ? (
        <div data-testid="returns-error" className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
          <p className="text-sm font-semibold text-red-700">Couldn&apos;t load your returns.</p>
          <button onClick={load} data-testid="returns-retry" className="mt-3 inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold px-4 py-2 rounded-md">Retry</button>
        </div>
      ) : filtered.length === 0 ? (
        <div data-testid="returns-empty" className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center mb-4"><RotateCcw className="w-7 h-7" /></div>
          <p className="text-lg font-semibold text-slate-700">{search ? "No matching returns" : "No returns yet"}</p>
          <p className="text-sm text-slate-500 mt-1">{search ? "Try a different order number or customer name." : "When we receive and inspect a return, it'll show up here with photos."}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="returns-list">
          {filtered.map((r) => (
            <ReturnCard key={r.id} r={r} savingItem={savingItem}
              onDisposition={(itemId, v) => setDisposition(r.id, itemId, v)}
              onDownloadPhotos={downloadPhotos}
              onPhoto={(photos, index) => { setZoom(false); setLightbox({ photos, index }); }} />
          ))}
        </div>
      )}

      {lightbox && (() => {
        const photos = lightbox.photos || [];
        const n = photos.length;
        const idx = Math.min(lightbox.index || 0, n - 1);
        const go = (d, e) => { e?.stopPropagation(); setZoom(false); setLightbox((lb) => ({ ...lb, index: (lb.index + d + n) % n })); };
        return (
          <div className="fixed inset-0 z-[100] bg-black/90 flex flex-col items-center justify-center p-6" onClick={() => { setZoom(false); setLightbox(null); }} data-testid="return-lightbox">
            <button className="absolute top-4 right-4 text-white/80 hover:text-white p-2" onClick={() => { setZoom(false); setLightbox(null); }} data-testid="lightbox-close"><X className="w-8 h-8" /></button>
            {n > 1 && !zoom && (
              <>
                <button onClick={(e) => go(-1, e)} data-testid="lightbox-prev" className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors"><ChevronLeft className="w-7 h-7" /></button>
                <button onClick={(e) => go(1, e)} data-testid="lightbox-next" className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors"><ChevronRight className="w-7 h-7" /></button>
              </>
            )}
            <div className="flex flex-col items-center gap-4 min-h-0" onClick={(e) => e.stopPropagation()}>
              <div className={zoom ? "overflow-auto max-h-[82vh] max-w-[94vw] rounded-xl" : ""} style={{ touchAction: "pinch-zoom" }}>
                <img src={photoUrl(photos[idx])} alt="return" data-testid="lightbox-image"
                  onClick={() => setZoom((z) => !z)}
                  className={`rounded-xl select-none transition-transform ${zoom ? "max-w-none w-[190%] sm:w-[170%] cursor-zoom-out" : "max-h-[74vh] max-w-[90vw] cursor-zoom-in"}`} />
              </div>
              {!zoom && <p className="text-white/40 text-xs -mt-1">Click the photo to zoom · pinch on touch</p>}
              {n > 1 && !zoom && (
                <>
                  <span className="text-white/70 text-sm font-medium tabular-nums" data-testid="lightbox-counter">{idx + 1} / {n}</span>
                  <div className="flex items-center gap-2">
                    {photos.map((p, i) => (
                      <button key={p.id || i} onClick={() => { setZoom(false); setLightbox((lb) => ({ ...lb, index: i })); }} data-testid={`lightbox-thumb-${i}`}
                        className={`w-14 h-14 rounded-lg overflow-hidden border-2 transition-colors ${i === idx ? "border-orange-400" : "border-white/20 hover:border-white/50"}`}>
                        <img src={photoUrl(p)} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
